import React from "react";
import { Check, X } from "lucide-react";
import { todayISO, fmtDateTimeNL, datumKort } from "./afsprakenUtils";

const Marker = ({ state, onClick, testid }) => {
  const cls = {
    done: "bg-emerald-500 border-emerald-500 text-white",
    gemist: "bg-rose-50 border-rose-400 text-rose-600",
    wacht: "bg-blue-50 border-blue-400 text-blue-700",
    volgende: "bg-white border-blue-600 ring-2 ring-blue-200",
    open: "bg-white border-zinc-300",
  }[state];
  return (
    <button type="button" data-testid={testid} onClick={onClick} aria-label="Peilmoment afvinken"
      className={`h-6 w-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-transform duration-150 hover:scale-110 ${cls}`}>
      {state === "done" && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
      {state === "gemist" && <X className="h-3.5 w-3.5" strokeWidth={3} />}
    </button>
  );
};

const LocatieMeldt = ({ a, cid }) => {
  const date = cid;
  const r = a.blReported && a.blReported[cid];
  if (!r || !(r.reported || (r.note || "").trim())) return null;
  return (
    <div data-testid={`checkpoint-blnote-${a.id}-${date}`} className="mt-1.5 rounded border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-xs">
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold text-emerald-800" data-testid={r.reported ? `checkpoint-gemeld-${a.id}-${date}` : undefined}>Locatie meldt{r.reported ? " (gemeld)" : ""}:</span>
        {r.at && <span className="font-mono text-[10px] text-emerald-700">{fmtDateTimeNL(r.at)}</span>}
      </div>
      {(r.note || "").trim() && <p className="text-zinc-700 mt-0.5 whitespace-pre-line break-words">{r.note}</p>}
    </div>
  );
};

// v15 A3: het antwoord van de locatie bij de deadline (ook zonder deelpunten).
export const LocatieMeldtDeadline = ({ a }) => {
  const r = a.blReported && a.blReported.deadline;
  if (!r || !(r.reported || (r.note || "").trim())) return null;
  return (
    <div data-testid={`detail-deadline-antwoord-${a.id}`} className="rounded border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-xs">
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold text-emerald-800">Locatie meldt bij de deadline{r.reported ? " (gereed)" : ""}:</span>
        {r.at && <span className="font-mono text-[10px] text-emerald-700">{fmtDateTimeNL(r.at)}</span>}
      </div>
      {(r.note || "").trim() && <p className="text-zinc-700 mt-0.5 whitespace-pre-line break-words">{r.note}</p>}
    </div>
  );
};

export const CheckpointTimeline = ({ a, onToggle, onNoteChange, onNoteBlur }) => {
  const today = todayISO();
  const sorted = [...(a.checkpoints || [])].sort((x, y) => x.date.localeCompare(y.date));
  const volgende = sorted.find((c) => !c.done && c.date >= today)?.date;
  const cidOf = (c) => c.id || c.date;
  return (
    <ol data-testid={`checkpoints-${a.id}`}>
      {sorted.map((c, i) => {
        // v22 W3: gemeld door de locatie/afdeling (server: a.meldingen) = wacht op de OM, niet gemist
        const state = c.done ? "done" : c.date < today ? (a.meldingen?.[cidOf(c)] ? "wacht" : "gemist") : c.date === volgende ? "volgende" : "open";
        return (
          <li key={cidOf(c)} data-testid={`checkpoint-${a.id}-${cidOf(c)}`} className="flex gap-3">
            <div className="flex flex-col items-center">
              <Marker state={state} onClick={() => onToggle(a, cidOf(c))} testid={`checkpoint-toggle-${a.id}-${cidOf(c)}`} />
              {i < sorted.length - 1 && <span className="w-px flex-1 bg-zinc-200 my-1" />}
            </div>
            <div className="flex-1 min-w-0 pb-4">
              <div className="flex items-center gap-2 min-h-6 flex-wrap">
                <span title={c.date} className={`font-mono text-sm whitespace-nowrap ${state === "done" ? "text-zinc-500" : "text-zinc-900 font-semibold"}`}>{datumKort(c.date)}</span>
                {(c.wat || "").trim() && <span data-testid={`checkpoint-wat-${a.id}-${cidOf(c)}`} className={`text-sm break-words ${state === "done" ? "text-zinc-500" : "text-zinc-800"}`}>— {c.wat}</span>}
                {state === "gemist" && <span className="text-[10px] font-bold uppercase tracking-wider text-rose-600">Gemist</span>}
                {state === "wacht" && <span data-testid={`checkpoint-wacht-${a.id}-${cidOf(c)}`} className="text-[10px] font-bold uppercase tracking-wider text-blue-700">Wacht op OM</span>}
                {state === "volgende" && <span className="text-[10px] font-bold uppercase tracking-wider text-blue-700">Volgende</span>}
              </div>
              <input data-testid={`checkpoint-note-${a.id}-${cidOf(c)}`} value={c.note || ""} placeholder="Jouw notitie (OM)…"
                onChange={(e) => onNoteChange(a.id, cidOf(c), e.target.value)} onBlur={() => onNoteBlur(a.id)}
                className="mt-1 w-full text-xs px-2 py-1.5 rounded border border-zinc-200 focus:outline-none focus:ring-1 focus:ring-blue-600 text-zinc-800" />
              <LocatieMeldt a={a} cid={cidOf(c)} />
            </div>
          </li>
        );
      })}
    </ol>
  );
};
